import React, { useEffect, useMemo, useRef, useState } from 'react';
import { tp, useP } from '../i18n/useP';
import { useTranslation } from 'react-i18next';
import { getCfView } from '../api/cashFlow';
import { listBsLines } from '../api/financials_bs';
import { putForecastAssumption } from '../api/forecastAssumptions';
import { usePersistentValue } from '../hooks/usePersistentState';
import UnitSwitch from '../components/UnitSwitch';
import './CashFlowPage.css';


/* Formatage montant — '–' pour 0, sinon CHF ou kCHF selon l'unité. */
function fmtAmt(v, unit) {
  const raw = Math.round(Number(v || 0));
  if (raw === 0) return '–';
  const n = unit === 'kchf' ? Math.round(raw / 1000) : raw;
  return n.toLocaleString('de-CH');
}

function chevronIcon(open) {
  return (
    <svg className={`cf-chevron${open ? ' open' : ''}`} viewBox="0 0 16 16" width="12" height="12">
      <polyline points="4 6 8 10 12 6" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );
}

/* Annualise une vue CF (flows uniquement, pas la réconciliation). */
function annualize(view) {
  if (!view) return null;
  const cc = Math.max(view.closed_count || 1, 1);
  if (cc >= 12) return view;
  const f = 12 / cc;
  return {
    ...view,
    sections: view.sections.map(sec => {
      if (sec.code === 'reconciliation') return sec; // stocks — ne pas annualiser
      return {
        ...sec,
        total: Math.round(sec.total * f),
        rows: sec.rows.map(r => ({ ...r, amount: Math.round(r.amount * f) })),
      };
    }),
  };
}

/* `forecastView` — 12 mois adossés au bilan hybride, via le MÊME moteur serveur
   que la vue réelle (le cash reste dérivé du bilan). */
export default function CashFlowPage({ activeTenant, addToast, year, actualsOnly = false, forecastView = false, version = null, unit: unitProp, embedded = false, onReady, standard = false, monthsDetail = true, cutoff = null }) {
  const { t } = useTranslation();
  const p = useP();
  const MONTHS3 = useMemo(() => Array.from({ length: 12 }, (_, i) => t(`common:months.${i + 1}`)), [t]);
  const [viewCurr, setViewCurr] = useState(null);
  const [viewPrev, setViewPrev] = useState(null);
  const [loading, setLoading]   = useState(false);
  const [collapsedLocal, setCollapsed] = usePersistentValue(`cf_collapsed_${activeTenant?.id}`, { wcr: false, investing: false, financing: false });
  // BFR agrégé en 2 lignes (BFRE / autres éléments), dégroupables au clic —
  // même lecture que le cash flow du hub Prévisionnel (demande du 2026-08-11).
  const [wcrGrpLocal, setWcrGrp] = usePersistentValue(`cf_wcrgrp_${activeTenant?.id}`, { bfre: true, autres: true });
  // `standard` (impression) : disposition figée — tout est ouvert (BFR,
  // investissement, financement — demande du 2026-08-19).
  const collapsed = standard ? { wcr: false, investing: false, financing: false } : collapsedLocal;
  const wcrGrp = standard ? { bfre: false, autres: false } : wcrGrpLocal;   // PDF : tout le détail
  const toggleWcrGrp = (k) => setWcrGrp({ ...wcrGrp, [k]: !wcrGrp[k] });
  // Unité contrôlée par le parent (hub États financiers) si `unit` est fourni ;
  // sinon bascule locale (usage autonome : page Flux de trésorerie).
  const [unitLocal, setUnit] = usePersistentValue(`cf_unit_${activeTenant?.id}`, 'kchf');
  const unitControlled = unitProp !== undefined;
  const unit = unitControlled ? unitProp : unitLocal;
  const [emptyOpen, setEmptyOpen] = useState({});
  const [bsLines, setBsLines] = useState([]);
  const [cashStartOverride, setCashStartOverride] = useState(null);
  const cashInputRef = useRef(null);

  /* Année du dernier mois clôturé → détermine si on montre la colonne Prévision. */
  const lastClosed = activeTenant?.last_closed_period; // "YYYY-MM-01" ou null
  const realYear = lastClosed ? parseInt(lastClosed.substring(0, 4), 10) : null;
  const showForecast = !actualsOnly && realYear !== null && year === realYear;

  // Détail mensuel : toujours présent (écran ET impression `standard`) dès
  // qu'il y a des mois clôturés. Pas de bouton de bascule.
  const closed = viewCurr?.closed_count || 0;
  // `monthsDetail` (écran Cash flow) : détail mensuel débrayable — coupé, seuls
  // les totaux N-1 / N restent (la colonne Prévision annualisée aussi).
  const showMonths = closed > 0 && monthsDetail;

  function lsKey(y) { return `cf_cash_start_${activeTenant?.id}_${y}`; }

  function extractCashStart(view) {
    const recon = view?.sections?.find(s => s.code === 'reconciliation');
    return recon?.rows?.find(r => r.code === 'cash_start')?.amount ?? 0;
  }

  async function load(y) {
    if (!activeTenant?.id || !y) return;
    setLoading(true);
    const [rCurr, rPrev, rLines] = await Promise.all([
      // `cutoff` : arrêté d'affichage (Rapports, vue Réel) — le serveur borne la vue.
      getCfView(activeTenant.id, y, { ...(forecastView ? { forecast: true } : {}), ...(version ? { version } : {}), ...(cutoff ? { through: cutoff } : {}) }),
      getCfView(activeTenant.id, y - 1),
      listBsLines(activeTenant.id),
    ]);
    if (rCurr.error) addToast(rCurr.error, 'error');
    else {
      setViewCurr(rCurr.data);
      // Le solde saisissable est l'ouverture de N-1 (clé année y-1) ; défaut =
      // trésorerie au bilan N-2 (= cash_start de la vue N-1).
      const saved = localStorage.getItem(lsKey(y - 1));
      setCashStartOverride(saved !== null ? Number(saved) : extractCashStart(rPrev.data));
    }
    setViewPrev(rPrev.data || null);
    setBsLines(rLines.data || []);
    setLoading(false);
    if (onReady) onReady();
  }

  useEffect(() => { load(year); }, [activeTenant?.id, year, forecastView, version, cutoff]); // eslint-disable-line

  function toggle(key) { setCollapsed(c => ({ ...c, [key]: !c[key] })); }
  function toggleEmpty(code) { setEmptyOpen(o => ({ ...o, [code]: !o[code] })); }

  function handleCashStartSave(raw) {
    const n = parseFloat(String(raw).replace(/[\s''']/g, '').replace(',', '.')) || 0;
    setCashStartOverride(n);
    localStorage.setItem(lsKey(year - 1), String(n));   // ouverture N-1
  }

  function refreshCashStart() {
    if (!viewPrev) return;
    setCashStartOverride(extractCashStart(viewPrev));    // défaut = bilan N-2
    localStorage.removeItem(lsKey(year - 1));
  }

  /* Cessions d'actifs (saisie manuelle, mois clôturés de l'année vue). Reçoit un
     montant déjà en CHF, jamais négatif (produit encaissé). Reconstruit la map
     {mois: montant} depuis la ligne, met à jour le mois, upsert. */
  async function handleCessionSave(month, chf, row) {
    const n = Math.max(0, Math.round(Number(chf) || 0));
    const map = {};
    const cur = row?.months || {};
    Object.keys(cur).forEach(k => { const v = Math.round(Number(cur[k]) || 0); if (v > 0) map[k] = v; });
    if (n) map[String(month)] = n; else delete map[String(month)];
    const res = await putForecastAssumption(activeTenant.id, year, 'cessions_actual', map);
    if (res.error) { addToast(res.error, 'error'); return; }
    load(year);
  }

  /* Vue annualisée (Prévision). */
  const viewFcast = showForecast ? annualize(viewCurr) : null;

  /* Helpers d'accès aux sections. */
  const getTotal = (v, code) => v?.sections?.find(s => s.code === code)?.total ?? 0;
  const getSec   = (v, code) => v?.sections?.find(s => s.code === code);

  /* Réconciliation — chaînage de la trésorerie côté front.
     Seule l'OUVERTURE de N-1 est saisissable (cashStartOverride, défaut = bilan
     N-2). L'ouverture de N est dérivée = clôture de N-1 (obligatoire). */
  const recon         = viewCurr?.sections?.find(s => s.code === 'reconciliation');
  const netCf         = recon?.rows?.find(r => r.code === 'net_cf')?.amount ?? 0;
  const cashEndBs     = recon?.rows?.find(r => r.code === 'cash_end_bs')?.amount ?? 0;
  const reconPrev     = viewPrev?.sections?.find(s => s.code === 'reconciliation');
  const netCfPrev     = reconPrev?.rows?.find(r => r.code === 'net_cf')?.amount ?? 0;
  const cashEndBsPrev = reconPrev?.rows?.find(r => r.code === 'cash_end_bs')?.amount ?? 0;

  const openPrev  = cashStartOverride ?? 0;     // ouverture N-1 (saisissable)
  const closePrev = openPrev + netCfPrev;       // clôture N-1 (calculée)
  // Ouverture de N = liquidité BILAN au 31.12.N-1 (cash_start backend), et NON la
  // clôture *calculée* de N-1 : cette dernière traîne l'écart de réconciliation
  // propre à N-1 → il se propageait en écart constant sur tous les mois de N.
  const cashStartCurr = recon?.rows?.find(r => r.code === 'cash_start')?.amount ?? closePrev;
  const openCurr  = cashStartCurr;              // ouverture N = bilan déc. N-1 (ancré, réconcilie)
  const closeCurr = openCurr + netCf;           // clôture N (calculée)
  const gapPrev   = Math.round((closePrev - cashEndBsPrev) * 100) / 100;
  const gap       = Math.round((closeCurr - cashEndBs) * 100) / 100;

  /* Forecast net_cf = somme des flux annualisés (hors réconciliation). */
  const netCfFcast  = showForecast
    ? getTotal(viewFcast, 'operating') + getTotal(viewFcast, 'investing') + getTotal(viewFcast, 'financing')
    : 0;
  const closeFcast  = openCurr + netCfFcast;

  /* Free Cash Flow */
  const fcfPrev  = getTotal(viewPrev,  'operating') + getTotal(viewPrev,  'investing');
  const fcf      = getTotal(viewCurr, 'operating') + getTotal(viewCurr, 'investing');
  const fcfFcast = showForecast
    ? getTotal(viewFcast, 'operating') + getTotal(viewFcast, 'investing')
    : 0;

  const unclassified = viewCurr?.meta?.unclassified_labels || [];

  /* ── Colonnes : N-1 | N (réel YTD) | [mois réalisés de N] | [N prévision] ────
     Le détail mensuel (showMonths) insère une colonne par mois clôturé, APRÈS la
     colonne annuelle N. Chaque colonne porte ses scalaires (variation nette, FCF,
     chaînage trésorerie) ; les flux mensuels viennent de section.months /
     row.months (réel, fournis par le backend). */
  const reconMonths = (code) => recon?.rows?.find(r => r.code === code)?.months || {};
  const netM    = reconMonths('net_cf');
  const cashBsM = reconMonths('cash_end_bs');
  let cum = 0; const openM = {}, closeM = {};
  for (let m = 1; m <= closed; m++) { openM[m] = openCurr + cum; cum += Number(netM[m] || 0); closeM[m] = openCurr + cum; }

  const cols = [
    { key: 'prev', label: t('phrases:p_964e49c9e4'), ytd: false, cls: undefined, view: viewPrev, month: 0,
      netcf: netCfPrev, fcf: fcfPrev, cashOpen: openPrev, cashClose: closePrev, cashBilan: cashEndBsPrev, gap: gapPrev, editableOpen: true },
    { key: 'curr', label: forecastView ? t('phrases:p_a153d367ca') : t('phrases:p_964e49c9e4'), ytd: true, cls: forecastView ? 'fcast' : undefined, view: viewCurr, month: 0,
      netcf: netCf, fcf, cashOpen: openCurr, cashClose: closeCurr, cashBilan: cashEndBs, gap, openTitle: p('Obligatoire = clôture N-1') },
  ];
  // Vue prévisionnelle en « Totaux » : 3 colonnes — N-1 réel · N RÉEL (cumul des
  // mois clos, sommé depuis les mois de la même vue) · N prévision (déc.).
  const realClosed = (forecastView && realYear === year && lastClosed)
    ? parseInt(lastClosed.substring(5, 7), 10) : 0;
  if (forecastView && !monthsDetail && realClosed > 0) {
    const sumM = (code, upto) => {
      const mm = getSec(viewCurr, code)?.months || {};
      let sm = 0; for (let m = 1; m <= upto; m++) sm += Number(mm[m] || 0);
      return sm;
    };
    let cumR = 0; for (let m = 1; m <= realClosed; m++) cumR += Number(netM[m] || 0);
    cols.splice(1, 0, {
      key: 'realytd', label: t('phrases:p_964e49c9e4'), ytd: false, sumTo: realClosed,
      view: viewCurr, month: 0,
      netcf: cumR, fcf: sumM('operating', realClosed) + sumM('investing', realClosed),
      cashOpen: openCurr, cashClose: openCurr + cumR,
      cashBilan: Number(cashBsM[realClosed] || 0),
      gap: Math.round((openCurr + cumR - Number(cashBsM[realClosed] || 0)) * 100) / 100,
    });
  }
  // Détail mensuel : un espace blanc (colonne spacer) puis une colonne par mois
  // réalisé. La colonne « Prévision » (annualisée) est masquée dans cette vue —
  // structure voulue : libellé | 2025 réel | 2026 réel | (espace) | mois.
  // `standard` (impression) : gabarit FIXE 12 mois — les mois non clôturés
  // restent vides (« – »), la mise en page ne bouge pas d'un mois à l'autre.
  if (showMonths) {
    cols.push({ key: 'gap', spacer: true });
    const mMax = standard ? 12 : closed;
    for (let m = 1; m <= mMax; m++) {
      const real = m <= closed;
      cols.push({
        key: `m${m}`, label: MONTHS3[m - 1], ytd: false, cls: undefined, isMonth: true, view: viewCurr, month: m,
        netcf: real ? Number(netM[m] || 0) : null,
        fcf: real ? Number(getSec(viewCurr, 'operating')?.months?.[m] || 0) + Number(getSec(viewCurr, 'investing')?.months?.[m] || 0) : null,
        cashOpen: real ? openM[m] : null, cashClose: real ? closeM[m] : null,
        cashBilan: real ? Number(cashBsM[m] || 0) : null,
        gap: real ? Math.round((closeM[m] - Number(cashBsM[m] || 0)) * 100) / 100 : null,
      });
    }
  } else if (showForecast && monthsDetail) {
    cols.push({ key: 'fcast', label: t('phrases:p_a153d367ca'), ytd: false, cls: 'fcast', view: viewFcast, month: 0,
      netcf: netCfFcast, fcf: fcfFcast, cashOpen: openCurr, cashClose: closeFcast, cashBilan: null, gap: null });
  }
  const nCols = cols.length;

  // Valeur d'une colonne pour une section / une ligne (annuel ou mois selon `month`).
  const sumUpTo = (obj, upto) => {
    const mm = obj?.months || {};
    let sm = 0; for (let m = 1; m <= upto; m++) sm += Number(mm[m] || 0);
    return sm;
  };
  const colTotal = (c, code) => (c.sumTo ? sumUpTo(getSec(c.view, code), c.sumTo)
    : (c.month ? getSec(c.view, code)?.months?.[c.month] : getSec(c.view, code)?.total)) ?? 0;
  const colAmt   = (c, code, idx) => (c.sumTo ? sumUpTo(getSec(c.view, code)?.rows?.[idx], c.sumTo)
    : (c.month ? getSec(c.view, code)?.rows?.[idx]?.months?.[c.month] : getSec(c.view, code)?.rows?.[idx]?.amount)) ?? 0;

  /* ── Composants liés à l'unité (closure sur `unit`) ──────────────────────── */
  function fmt(v) { return fmtAmt(v, unit); }

  function AmtCell({ value, cls, ytd }) {
    const n = Number(value || 0);
    const base = cls || (n < 0 ? 'neg' : n > 0 ? 'plain' : 'zero');
    return <td className={`cf-num cf-num--${base}${ytd ? ' cf-num--ytd' : ''}`}>{fmt(value)}</td>;
  }

  // Cellule de flux d'une colonne : vide (espace blanc) si colonne spacer, sinon
  // un AmtCell (ton navy pour les totaux, signe pour les détails).
  function flowCell(c, value, total) {
    if (c.spacer) return <td key={c.key} className="cf-col-gap" />;
    return <AmtCell key={c.key} value={value} cls={total ? (c.cls || 'navy') : c.cls} ytd={c.ytd} />;
  }

  function CollapseHeader({ label, open, onToggle, sectionCode }) {
    return (
      <tr className="cf-row--section cf-row--toggle" onClick={onToggle}>
        <td>{chevronIcon(open)}<span>{label}</span></td>
        {open
          ? cols.map(c => <td key={c.key} className={c.spacer ? 'cf-col-gap' : undefined} />)
          : cols.map(c => flowCell(c, colTotal(c, sectionCode), true))}
      </tr>
    );
  }

  /* Rendu d'une ligne de détail. La ligne « Cessions d'actifs » (saisie manuelle)
     rend un champ éditable dans chaque colonne mensuelle clôturée de l'année vue ;
     ailleurs (cumuls N-1 / N, prévision) elle reste en lecture seule. */
  function detailRow(code, row, idx) {
    const editable = row.code === 'cessions_actual';
    return (
      <tr key={row.code || idx} className="cf-row--detail">
        <td className="cf-label-indent">
          {row.label}
        </td>
        {cols.map(c => (editable && c.isMonth && !c.spacer)
          ? <td key={c.key} className="cf-num cf-num--edit">
              <CessionInput value={colAmt(c, code, idx)} unit={unit}
                onSave={chf => handleCessionSave(c.month, chf, row)} />
            </td>
          : flowCell(c, colAmt(c, code, idx), false))}
      </tr>
    );
  }

  /* Section de détail : lignes actives + postes sans mouvement repliés + sous-total.
     La ligne cessions reste toujours active (éditable même à zéro). */
  function DetailRows({ code, subtotalLabel, only = null }) {
    const rows = getSec(viewCurr, code)?.rows || [];
    const isEmpty = idx => rows[idx]?.code !== 'cessions_actual'
      && cols.every(c => c.spacer || !Math.round(colAmt(c, code, idx) || 0));
    const active = [], empty = [];
    rows.forEach((row, idx) => {
      if (only && !only(row)) return;
      (isEmpty(idx) ? empty : active).push({ row, idx });
    });
    const open = emptyOpen[code];
    return <>
      {active.map(({ row, idx }) => detailRow(code, row, idx))}
      {empty.length > 0 && (
        <tr className="cf-row--fold" onClick={() => toggleEmpty(code)}>
          <td colSpan={nCols + 1}>
            {open ? p('− Masquer') : `+ ${empty.length} ${empty.length > 1 ? p('postes') : p('poste')}`} {p('sans mouvement')}
          </td>
        </tr>
      )}
      {open && empty.map(({ row, idx }) => detailRow(code, row, idx))}
      {subtotalLabel != null && (
      <tr className="cf-row--subtotal">
        <td>{subtotalLabel}</td>
        {cols.map(c => flowCell(c, colTotal(c, code), true))}
      </tr>
      )}
    </>;
  }

  // Ligne agrégée BFRE / autres : somme des Δ de son périmètre, cliquable pour
  // dégrouper (le détail garde l'ordre du tableau).
  function wcrGroupRow(label, key, pred, fullLabel = null) {
    const rows = getSec(viewCurr, 'wcr')?.rows || [];
    const idxs = rows.map((r, i) => (pred(r) ? i : -1)).filter((i) => i >= 0);
    const val = (c) => idxs.reduce((sm, i) => sm + Number(colAmt(c, 'wcr', i) || 0), 0);
    return (
      <tr className="cf-row--subtotal cf-row--wcrgrp" onClick={() => toggleWcrGrp(key)}
        title={wcrGrp[key] ? p('Cliquer pour voir le détail') : p('Cliquer pour regrouper')}>
        <td title={fullLabel || undefined}>{label}<span className="cf-groupmark">{wcrGrp[key] ? '＋' : '－'}</span></td>
        {cols.map(c => flowCell(c, val(c), true))}
      </tr>
    );
  }

  return (
    <div className={`page-table-layout is-fluid${embedded ? ' is-embedded' : ''}`}>
      {loading && <p className="cf-loading">{p('Chargement…')}</p>}
      {!loading && !viewCurr && (
        <p className="cf-loading">{t('phrases:p_4b689d3a79', { year })}</p>
      )}

      {!loading && viewCurr && (
        <div className={`cf-page${embedded ? ' cf-page--bare' : ''}`}>

          {/* ═══ CARD TABLEAU ═══ */}
          <div className={`cf-card${embedded ? ' cf-card--bare' : ''}`}>
            <div className="cf-card-head cf-card-head--flex">
              <div className="cf-head-tools">
                {unclassified.length > 0 && (
                  <span className="cf-mapping-count" title={t('phrases:p_4483f7865c')}>
                    {unclassified.length} {unclassified.length > 1 ? p('non classés') : p('non classé')}
                  </span>
                )}
              </div>
              {!unitControlled && (
                <div className="cf-head-controls">
                  <UnitSwitch value={unit} onChange={setUnit} />
                </div>
              )}
            </div>
            <div className="cf-table-wrap">
            <table className="cf-table">
              <colgroup>
                <col className="cf-col-label" />
                {cols.map(c => <col key={c.key} className={c.spacer ? 'cf-col-gap' : `cf-col-amount${c.isMonth ? ' cf-col-month' : ''}`} />)}
              </colgroup>
              <thead>
                <tr className="cf-year-row">
                  <th />
                  {(() => {
                    // 2025 / 2026 au-dessus de leur colonne cumulé (alignés à droite),
                    // puis un titre groupé « Évolution AAAA » au-dessus des mois.
                    const out = [];
                    for (let i = 0; i < cols.length; i++) {
                      const c = cols[i];
                      if (c.spacer) { out.push(<th key={c.key} className="cf-col-gap" />); continue; }
                      if (c.isMonth) {
                        let j = i; while (j + 1 < cols.length && cols[j + 1].isMonth) j++;
                        const span = j - i + 1;
                        // Bandeau standard RÉALISÉ / PRÉVISIONNEL (2026-08-20) :
                        // en vue prévisionnelle, la frontière = clôture du tenant.
                        if (forecastView && realClosed > 0 && realClosed < span) {
                          out.push(<th key="evo-real" className="lk-band lk-band--real" colSpan={realClosed}>{t('finance:reports.bandReal')}</th>);
                          out.push(<th key="evo-fore" className="lk-band lk-band--fore" colSpan={span - realClosed}>{t('finance:reports.bandFore')}</th>);
                        } else {
                          out.push(<th key="evo" className="cf-year-grp cf-year-evo" colSpan={span}>{t('phrases:p_5ee9bde133', { year })}</th>);
                        }
                        i = j; continue;
                      }
                      const yr = c.key === 'prev' ? year - 1 : (c.key === 'curr' || c.key === 'realytd') ? year : '';
                      out.push(<th key={c.key} className="cf-year-grp cf-year-grp--right">{yr}</th>);
                    }
                    return out;
                  })()}
                </tr>
                <tr className="cf-thead-sub">
                  <td />
                  {cols.map(c => (c.spacer
                    ? <td key={c.key} className="cf-col-gap" />
                    : <td key={c.key} className={c.ytd ? 'cf-th-ytd' : c.cls === 'fcast' ? 'cf-th-sub-fcast' : c.isMonth ? 'cf-th-month' : undefined}>
                        {c.label}
                      </td>
                  ))}
                </tr>
              </thead>
              <tbody>

                {/* ═══ CAF ═══ */}
                {(getSec(viewCurr, 'caf')?.rows || []).map((row, idx) => (
                  <tr key={row.code || idx} className="cf-row--detail">
                    <td className="cf-label-indent">{row.label}</td>
                    {cols.map(c => flowCell(c, colAmt(c, 'caf', idx), false))}
                  </tr>
                ))}
                <tr className="cf-row--subtotal">
                  <td>{t('phrases:p_4544d7142a')}</td>
                  {cols.map(c => flowCell(c, colTotal(c, 'caf'), true))}
                </tr>

                {/* ── BFR : BFRE puis autres éléments, agrégés par défaut ── */}
                {!wcrGrp.bfre && <DetailRows code="wcr" only={(r) => !!r.is_bfre} subtotalLabel={null} />}
                {wcrGroupRow(p('± Variation BFRE'), 'bfre', (r) => !!r.is_bfre)}
                {!wcrGrp.autres && <DetailRows code="wcr" only={(r) => !r.is_bfre} subtotalLabel={null} />}
                {wcrGroupRow(p('Autres éléments FR'), 'autres', (r) => !r.is_bfre, p('Autres éléments du fonds de roulement'))}
                <tr className="cf-row--subtotal">
                  <td>{p('Variation nette du BFR')}</td>
                  {cols.map(c => flowCell(c, colTotal(c, 'wcr'), true))}
                </tr>

                {/* ── Flux opérationnel ── */}
                <tr className="cf-row--total">
                  <td>{p('Flux de trésorerie opérationnel')}</td>
                  {cols.map(c => flowCell(c, colTotal(c, 'operating'), true))}
                </tr>

                <tr className="cf-row--spacer"><td colSpan={nCols + 1} /></tr>

                {/* ═══ INVESTISSEMENT ═══ */}
                <CollapseHeader label={p("Flux d'investissement")}
                  open={!collapsed.investing} onToggle={() => toggle('investing')} sectionCode="investing" />
                {!collapsed.investing && (
                  <DetailRows code="investing" subtotalLabel={t('phrases:p_af3b9472a8')} />
                )}

                {/* ── Free Cash Flow ── */}
                <tr className="cf-row--fcf-spacer"><td colSpan={nCols + 1} /></tr>
                <tr className="cf-row--total">
                  <td>{p('Free cash flow')}</td>
                  {cols.map(c => flowCell(c, c.fcf, true))}
                </tr>

                <tr className="cf-row--spacer"><td colSpan={nCols + 1} /></tr>

                {/* ═══ FINANCEMENT ═══ */}
                <CollapseHeader label={p('Flux de financement')}
                  open={!collapsed.financing} onToggle={() => toggle('financing')} sectionCode="financing" />
                {!collapsed.financing && (
                  <DetailRows code="financing" subtotalLabel={t('phrases:p_392ab550d2')} />
                )}

                {/* ── Variation nette ── */}
                <tr className="cf-row--net-cf">
                  <td>{p('Variation nette de trésorerie')}</td>
                  {cols.map(c => flowCell(c, c.netcf, true))}
                </tr>

                {/* ═══ SOLDE DE TRÉSORERIE (bornes du flux, pas une réconciliation) ═══ */}
                <tr className="cf-row--recon-head">
                  <td colSpan={nCols + 1}>{t('phrases:p_2eb9bf52dd')}</td>
                </tr>

                <tr className="cf-row--cash-key">
                  <td className="cf-label-indent">
                    {p('Trésorerie début de période')}
                    <button className="cf-refresh-btn" onClick={refreshCashStart} title={p('Reprendre du bilan N-2 (31.12)')}>↺</button>
                  </td>
                  {cols.map(c => (c.spacer
                    ? <td key={c.key} className="cf-col-gap" />
                    : <td key={c.key} className={`cf-num${c.ytd ? ' cf-num--ytd' : ''}${c.cls === 'fcast' ? ' cf-num--fcast' : ''}`} title={c.openTitle}>
                        {c.editableOpen
                          ? <CashStartInput ref={cashInputRef} value={c.cashOpen} unit={unit} onSave={handleCashStartSave} />
                          : fmt(c.cashOpen)}
                      </td>
                  ))}
                </tr>

                <tr className="cf-row--cash-key cf-row--cash-final">
                  <td className="cf-label-indent">{p('Trésorerie fin de période')}</td>
                  {cols.map(c => (c.spacer
                    ? <td key={c.key} className="cf-col-gap" />
                    : <td key={c.key} className={`cf-num${c.ytd ? ' cf-num--ytd' : ''}${c.cls === 'fcast' ? ' cf-num--fcast' : ''} cf-num--${Number(c.cashClose) >= 0 ? 'plain' : 'neg'}`}>
                        {fmt(c.cashClose)}
                      </td>
                  ))}
                </tr>

              </tbody>
            </table>
            </div>

          </div>

          {/* ── Réconciliation avec le bilan — HORS card, sous la card (table
              auto-alignée par colgroup, colonnes calées sur le tableau ci-dessus). ── */}
          <div className="cf-recon-block">
            <div className="cf-recon-cap">{p('Réconciliation avec le bilan')}</div>
            <table className="cf-table cf-recon-table">
              <colgroup>
                <col className="cf-col-label" />
                {cols.map(c => <col key={c.key} className={c.spacer ? 'cf-col-gap' : `cf-col-amount${c.isMonth ? ' cf-col-month' : ''}`} />)}
              </colgroup>
              <tbody>
                {/* Périodes — repères discrets alignés sur les colonnes de la card. */}
                <tr className="cf-row--recon-periods">
                  <td />
                  {cols.map(c => (c.spacer
                    ? <td key={c.key} className="cf-col-gap" />
                    : <td key={c.key} className="cf-num">
                        {c.isMonth || c.cls === 'fcast' ? c.label : (c.key === 'prev' ? year - 1 : year)}
                      </td>
                  ))}
                </tr>
                <tr className="cf-row--recon">
                  <td className="cf-label-indent">{p('Trésorerie au bilan (liquidités)')}</td>
                  {cols.map(c => (c.spacer
                    ? <td key={c.key} className="cf-col-gap" />
                    : <td key={c.key} className="cf-num cf-num--zero">{c.cashBilan == null ? '–' : fmt(c.cashBilan)}</td>
                  ))}
                </tr>
                <tr className="cf-row--gap">
                  <td className="cf-label-indent">{p('Écart')}</td>
                  {cols.map(c => (c.spacer
                    ? <td key={c.key} className="cf-col-gap" />
                    : c.gap == null
                      ? <td key={c.key} />
                      : <td key={c.key} className={`cf-num ${Math.abs(c.gap) < 1 ? 'cf-num--gap-ok' : 'cf-num--gap-err'}`}>
                          {Math.abs(c.gap) < 1 ? p('= 0 ✓') : fmt(c.gap)}
                        </td>
                  ))}
                </tr>
              </tbody>
            </table>
          </div>

          {unclassified.length > 0 && (
            <div className="cf-unclassified-warn">
              <strong>{unclassified.length} {unclassified.length > 1 ? p('lignes non classées') : p('ligne non classée')}</strong> — {unclassified.join(', ')}.
              <span className="cf-warn-cta">{p('À classer dans Paramètres › Catégories de flux.')}</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}


/* Cellule mensuelle éditable pour un produit de cession. La saisie se fait dans
   l'unité affichée (kCHF ou CHF) et jamais en négatif (produit encaissé). */
function CessionInput({ value, unit, onSave }) {
  const p = useP();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft]     = useState('');
  const div = unit === 'chf' ? 1 : 1000;   // valeur stockée en CHF, saisie dans l'unité

  function start() {
    setDraft(value ? Math.round(value / div).toLocaleString('de-CH') : '');
    setEditing(true);
  }
  function commit() {
    setEditing(false);
    const raw = parseFloat(String(draft).replace(/[\s''']/g, '').replace(',', '.')) || 0;
    onSave(Math.max(0, Math.round(raw)) * div);   // → CHF, jamais négatif
  }

  if (editing) {
    return (
      <input
        className="cf-cess-input"
        autoFocus
        value={draft}
        onChange={e => setDraft(e.target.value.replace('-', ''))}
        onBlur={commit}
        onKeyDown={e => { if (e.key === 'Enter') commit(); if (e.key === 'Escape') setEditing(false); }}
      />
    );
  }

  const n = Number(value || 0);
  return (
    <span
      className={`cf-cess-val${n !== 0 ? ' has-val' : ' no-val'}`}
      onClick={start}
      title={`${p('Cliquer pour saisir le produit de cession')} (${unit === 'chf' ? 'CHF' : 'kCHF'})`}
    >
      {n !== 0 ? fmtAmt(n, unit) : '–'}
    </span>
  );
}

const CashStartInput = React.forwardRef(function CashStartInput({ value, unit, onSave }, ref) {
  const p = useP();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft]     = useState('');

  function start() {
    setDraft(value ? Math.round(value).toLocaleString('de-CH') : '');
    setEditing(true);
  }
  function commit() {
    setEditing(false);
    onSave(draft);
  }

  if (editing) {
    return (
      <input
        ref={ref}
        className="cf-cash-input"
        autoFocus
        value={draft}
        onChange={e => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={e => { if (e.key === 'Enter') commit(); if (e.key === 'Escape') setEditing(false); }}
      />
    );
  }

  const n = Number(value || 0);
  return (
    <span
      className={`cf-cash-val${n !== 0 ? ' has-val' : ' no-val'}`}
      onClick={start}
      title={p('Cliquer pour modifier (CHF)')}
    >
      {n !== 0 ? fmtAmt(n, unit) : p('– Saisir –')}
    </span>
  );
});
